import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addComment,
  ApiError,
  blockUser,
  deleteComment,
  loadComments,
  purgeUserComments,
  setCommentLike,
  type CommentPage,
  type NovelComment
} from "../apiClient.js";
import { Avatar } from "./Avatar.js";
import { DeleteCommentModal, type DeleteCommentOptions } from "./DeleteCommentModal.js";
import { ReportDialog } from "./ReportDialog.js";
import { FlagIcon } from "./FlagIcon.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { AutoTextarea } from "./AutoTextarea.js";

type SortMode = "date" | "likes";

/**
 * Reader comments under a novel: a composer plus a one-level-deep thread. The
 * component owns its own data (loads on mount, mutates in place). It never
 * blocks the page — a load failure just shows an inline error.
 *
 * Threads arrive a page at a time, newest first, each with all of its replies;
 * "Show more comments" appends the next, older page. Sorting by likes ranks
 * every thread, so it first loads the pages still to come (as the catalog does
 * for a sort other than its natural one) instead of ranking only what's loaded.
 */
export function CommentsSection({
  novelId,
  novelOwnerId,
  currentUserId,
  onOpenAuthor,
  onRequireAccount
}: {
  novelId: string;
  novelOwnerId: string;
  /** The signed-in user's id, or null when they can't comment (must sign in). */
  currentUserId: string | null;
  onOpenAuthor: (ownerId: string, username?: string) => void;
  /** Signed out: the hint becomes a button that opens sign-up (and returns here). */
  onRequireAccount?: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const [comments, setComments] = useState<NovelComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Where the next, older page of threads starts; null once everything is here.
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  // The server's live-comment count with the first page, kept current as this
  // reader posts and deletes; null when the API sent none (count what's loaded).
  const [total, setTotal] = useState<number | null>(null);
  const [sort, setSort] = useState<SortMode>("date");
  // The comment whose delete confirmation popup is open, or null.
  const [deleteTarget, setDeleteTarget] = useState<NovelComment | null>(null);
  const [reportTarget, setReportTarget] = useState<string | null>(null);
  // The novel on screen, so a page that arrives after the reader moved on is dropped.
  const shownNovelId = useRef(novelId);
  // A page is on its way (a ref: the effect below must not start a second one).
  const loadingMoreRef = useRef(false);
  // Ids of the threads a click on "Show more" just brought in: focus moves to
  // the first of them, or the button that had focus vanishes with the last page.
  const focusAfterLoad = useRef<Set<string> | null>(null);
  const threadListRef = useRef<HTMLUListElement>(null);

  function showFirstPage(page: CommentPage) {
    setComments(page.comments);
    setNextCursor(page.nextCursor);
    setTotal(page.total ?? null);
    setMoreFailed(false);
  }

  useEffect(() => {
    let cancelled = false;
    shownNovelId.current = novelId;
    setLoading(true);
    setError(false);
    setNextCursor(null);
    void loadComments(novelId)
      .then((page) => {
        if (!cancelled) showFirstPage(page);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [novelId]);

  // Top-level comments sorted by the chosen mode, each with its replies grouped
  // underneath (replies always stay chronological within a thread).
  const threads = useMemo(() => {
    // A deleted reply leaves nothing behind — drop it entirely, so only live
    // replies keep a thread (and its tombstoned parent) on screen.
    const repliesByParent = new Map<string, NovelComment[]>();
    for (const comment of comments) {
      if (comment.parent_id && !comment.deleted) {
        const list = repliesByParent.get(comment.parent_id) ?? [];
        list.push(comment);
        repliesByParent.set(comment.parent_id, list);
      }
    }
    // A deleted top-level comment survives only as a tombstone that anchors its
    // still-visible replies; once none remain it disappears completely too.
    const tops = comments.filter(
      (comment) =>
        comment.parent_id === null &&
        (!comment.deleted || (repliesByParent.get(comment.id)?.length ?? 0) > 0)
    );
    const byDateDesc = (a: NovelComment, b: NovelComment) => b.created_at.localeCompare(a.created_at);
    const sorted = [...tops].sort(
      sort === "likes" ? (a, b) => b.likes - a.likes || byDateDesc(a, b) : byDateDesc
    );
    return sorted.map((top) => ({ comment: top, replies: repliesByParent.get(top.id) ?? [] }));
  }, [comments, sort]);

  const totalCount = total ?? comments.filter((comment) => !comment.deleted).length;

  // `focusNew`: the reader asked for this page, so keyboard and screen-reader
  // focus follows to its first thread. Loading everything for the likes ranking
  // moves nothing.
  const loadMore = useCallback(
    async (focusNew = false) => {
      if (nextCursor === null || loadingMoreRef.current) return;
      const requestedFor = novelId;
      loadingMoreRef.current = true;
      setLoadingMore(true);
      setMoreFailed(false);
      try {
        const page = await loadComments(novelId, nextCursor);
        if (shownNovelId.current !== requestedFor) return;
        if (focusNew) focusAfterLoad.current = new Set(page.comments.map((comment) => comment.id));
        // Only older threads come back, so nothing on screen repeats; the id
        // check still guards against a thread the reader already has.
        setComments((current) => {
          const shown = new Set(current.map((comment) => comment.id));
          return [...current, ...page.comments.filter((comment) => !shown.has(comment.id))];
        });
        setNextCursor(page.nextCursor);
      } catch {
        if (shownNovelId.current === requestedFor) setMoreFailed(true);
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    },
    [novelId, nextCursor]
  );

  // The likes ranking needs every thread: keep loading pages until none are
  // left. A failed page ends the wait (the loaded threads show, with the retry
  // button), and a successful retry resumes loading the rest.
  const rankingNeedsAll = sort === "likes" && nextCursor !== null;
  const rankingPending = rankingNeedsAll && !moreFailed;
  useEffect(() => {
    if (rankingNeedsAll && !loading && !error && !loadingMore && !moreFailed) void loadMore();
  }, [rankingNeedsAll, loading, error, loadingMore, moreFailed, loadMore]);

  // The page the reader asked for is on screen: move focus to its first thread.
  useEffect(() => {
    const wanted = focusAfterLoad.current;
    if (!wanted) return;
    focusAfterLoad.current = null;
    const shown = threadListRef.current?.querySelectorAll<HTMLElement>("li[data-thread-id]") ?? [];
    Array.from(shown)
      .find((thread) => wanted.has(thread.dataset.threadId ?? ""))
      ?.focus({ preventScroll: true });
  }, [comments]);

  // A comment or reply this reader just posted shows at once, in its place.
  function appendPosted(created: NovelComment) {
    setComments((current) => [...current, created]);
    setTotal((count) => (count === null ? null : count + 1));
  }

  // A tombstone has nothing left to delete or moderate (and no author to act on).
  function canDelete(comment: NovelComment): boolean {
    return (
      !comment.deleted &&
      currentUserId !== null &&
      (comment.user_id === currentUserId || novelOwnerId === currentUserId)
    );
  }

  // The novel owner can bulk-remove another user's comments (not their own).
  function canModerate(comment: NovelComment): boolean {
    return (
      !comment.deleted &&
      comment.user_id !== "" &&
      currentUserId !== null &&
      novelOwnerId === currentUserId &&
      comment.user_id !== currentUserId
    );
  }

  // Confirmed from the delete popup. The owner may also purge the user's whole
  // history and/or block them; a plain delete just tombstones this one comment.
  async function handleConfirmDelete(comment: NovelComment, options: DeleteCommentOptions) {
    if (options.block) await blockUser(comment.user_id);
    if (options.deleteAll) {
      await purgeUserComments(novelId, comment.user_id);
      // A purge removes rows on every page: start again from the first one.
      showFirstPage(await loadComments(novelId));
      return;
    }
    const tombstone = await deleteComment(novelId, comment.id);
    setComments((current) => current.map((c) => (c.id === comment.id ? tombstone : c)));
    setTotal((count) => (count === null ? null : count - 1));
  }

  // Optimistically flip the like, reconcile with the server, roll back on error.
  async function handleToggleLike(comment: NovelComment) {
    const next = !comment.liked_by_me;
    setComments((current) =>
      current.map((c) =>
        c.id === comment.id ? { ...c, liked_by_me: next, likes: c.likes + (next ? 1 : -1) } : c
      )
    );
    try {
      const updated = await setCommentLike(novelId, comment.id, next);
      setComments((current) => current.map((c) => (c.id === comment.id ? updated : c)));
    } catch {
      setComments((current) =>
        current.map((c) =>
          c.id === comment.id ? { ...c, liked_by_me: !next, likes: c.likes + (next ? -1 : 1) } : c
        )
      );
    }
  }

  return (
    <section className="comments" aria-labelledby="comments-heading">
      <h2 id="comments-heading" className="comments-heading">
        {totalCount > 0 ? t("comments.titleCount", { count: totalCount }) : t("comments.title")}
      </h2>

      {currentUserId ? (
        <CommentComposer
          onSubmit={async (body) => {
            appendPosted(await addComment(novelId, body));
          }}
        />
      ) : onRequireAccount ? (
        <button type="button" className="secondary comments-signin-button" onClick={onRequireAccount}>
          {t("comments.signUpToComment")}
        </button>
      ) : (
        <p className="comments-signin">{t("comments.signInToComment")}</p>
      )}

      {threads.length > 1 && (
        <div className="comments-sort" role="group" aria-label={t("comments.sortLabel")}>
          <button
            type="button"
            className={sort === "date" ? "comments-sort-btn is-active" : "comments-sort-btn"}
            aria-pressed={sort === "date"}
            onClick={() => setSort("date")}
          >
            {t("comments.sortByDate")}
          </button>
          <button
            type="button"
            className={sort === "likes" ? "comments-sort-btn is-active" : "comments-sort-btn"}
            aria-pressed={sort === "likes"}
            onClick={() => setSort("likes")}
          >
            {t("comments.sortByLikes")}
          </button>
        </div>
      )}

      {(loading || (rankingPending && !error)) && <CommentSkeletons label={t("comments.loading")} />}
      {error && !loading && <p className="comments-status comments-status--error">{t("comments.error")}</p>}
      {/* Not while older pages remain: a page of only tombstones shows nothing, yet more may follow. */}
      {!loading && !error && threads.length === 0 && nextCursor === null && (
        <p className="comments-status">{t("comments.empty")}</p>
      )}

      {/* Hidden while the likes ranking waits for the rest of the pages: a partial ranking would misplace threads. */}
      <ul className="comment-list" ref={threadListRef} hidden={rankingPending}>
        {threads.map(({ comment, replies }) => (
          <li key={comment.id} className="comment-thread" data-thread-id={comment.id} tabIndex={-1}>
            <CommentItem
              comment={comment}
              lang={lang}
              onOpenAuthor={onOpenAuthor}
              canReply={currentUserId !== null && !comment.deleted}
              canDelete={canDelete(comment)}
              canLike={currentUserId !== null}
              onDelete={() => setDeleteTarget(comment)}
              onReport={() => setReportTarget(comment.id)}
              onToggleLike={() => void handleToggleLike(comment)}
              onReply={async (body) => {
                appendPosted(await addComment(novelId, body, comment.id));
              }}
            />
            {replies.length > 0 && (
              <ul className="comment-replies">
                {replies.map((reply) => (
                  <li key={reply.id}>
                    <CommentItem
                      comment={reply}
                      lang={lang}
                      onOpenAuthor={onOpenAuthor}
                      canReply={false}
                      canDelete={canDelete(reply)}
                      canLike={currentUserId !== null}
                      onDelete={() => setDeleteTarget(reply)}
                      onReport={() => setReportTarget(reply.id)}
                      onToggleLike={() => void handleToggleLike(reply)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>

      {nextCursor !== null && !loading && !error && !rankingPending && (
        <div className="comments-more">
          {moreFailed && (
            <p className="comments-status comments-status--error" role="alert">
              {t("comments.error")}
            </p>
          )}
          {/* aria-disabled, not disabled: the button keeps keyboard focus while a page loads. */}
          <button
            type="button"
            className="secondary"
            aria-disabled={loadingMore || undefined}
            onClick={() => void loadMore(true)}
          >
            {loadingMore ? t("comments.loading") : t("comments.loadMore")}
          </button>
        </div>
      )}

      {deleteTarget && (
        <DeleteCommentModal
          canModerate={canModerate(deleteTarget)}
          onConfirm={(options) => handleConfirmDelete(deleteTarget, options)}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      {reportTarget && (
        <ReportDialog
          targetType="comment"
          targetId={reportTarget}
          novelId={novelId}
          onClose={() => setReportTarget(null)}
        />
      )}
    </section>
  );
}

/** Placeholder rows while comments load, with a status line for screen readers. */
function CommentSkeletons({ label }: { label: string }) {
  return (
    <>
      <p className="sr-only" role="status">
        {label}
      </p>
      <ul className="comment-list" aria-hidden="true">
        {[0, 1].map((index) => (
          <li key={index} className="comment-thread comment-thread--skeleton">
            <div className="comment-head">
              <span className="skeleton comment-skel-avatar" />
              <span className="skeleton comment-skel-name" />
            </div>
            <div className="skeleton comment-skel-line" />
            <div className="skeleton comment-skel-line comment-skel-line--short" />
          </li>
        ))}
      </ul>
    </>
  );
}

/** One comment row: avatar + name + time + body, with like/reply/delete affordances. */
function CommentItem({
  comment,
  lang,
  onOpenAuthor,
  canReply,
  canDelete,
  canLike,
  onDelete,
  onToggleLike,
  onReply,
  onReport
}: {
  comment: NovelComment;
  lang: string;
  onOpenAuthor: (ownerId: string, username?: string) => void;
  canReply: boolean;
  canDelete: boolean;
  canLike: boolean;
  onDelete: () => void;
  onToggleLike: () => void;
  onReply?: (body: string) => Promise<void>;
  onReport?: () => void;
}) {
  const t = useT();
  const [replying, setReplying] = useState(false);

  return (
    <div className={comment.deleted ? "comment comment--deleted" : "comment"}>
      <div className="comment-head">
        {/* A removed comment no longer reveals who wrote it. */}
        {!comment.deleted && (
          <>
            <Avatar
              name={comment.author.display_name}
              url={comment.author.avatar_url}
              size={26}
              className="comment-avatar"
            />
            <button type="button" className="comment-author" onClick={() => onOpenAuthor(comment.user_id, comment.author.username)}>
              {comment.author.display_name}
            </button>
          </>
        )}
        <time className="comment-time" dateTime={comment.created_at}>
          {formatRelativeTime(comment.created_at, lang)}
        </time>
      </div>
      <p className="comment-body">{comment.deleted ? t("comments.deleted") : comment.body}</p>
      <div className="comment-actions">
        {!comment.deleted && (
          <button
            type="button"
            className={comment.liked_by_me ? "comment-like is-liked" : "comment-like"}
            aria-pressed={comment.liked_by_me}
            aria-label={t("comments.likeAria")}
            disabled={!canLike}
            onClick={onToggleLike}
          >
            <span aria-hidden="true">{comment.liked_by_me ? "♥" : "♡"}</span>
            {comment.likes > 0 && <span className="comment-like-count">{comment.likes}</span>}
          </button>
        )}
        {canReply && onReply && (
          <button type="button" className="comment-action" onClick={() => setReplying((open) => !open)}>
            {t("comments.reply")}
          </button>
        )}
        {canDelete && !comment.deleted && (
          <button type="button" className="comment-action comment-action--danger" onClick={onDelete}>
            {t("comments.delete")}
          </button>
        )}
        {onReport && !comment.deleted && (
          <button
            type="button"
            className="comment-action comment-report"
            aria-label={t("report.action")}
            title={t("report.action")}
            onClick={onReport}
          >
            <FlagIcon size={14} />
          </button>
        )}
      </div>
      {replying && onReply && (
        <CommentComposer
          reply
          onSubmit={async (body) => {
            await onReply(body);
            setReplying(false);
          }}
          onCancel={() => setReplying(false)}
        />
      )}
    </div>
  );
}

/** Textarea + submit for a new comment or reply. Optimistic-friendly: the
 *  parent appends the returned comment; this just handles input + busy state. */
function CommentComposer({
  onSubmit,
  onCancel,
  reply = false
}: {
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
  reply?: boolean;
}) {
  const t = useT();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<
    "comments.error" | "comments.rateLimited" | "comments.blocked" | null
  >(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (reply) textareaRef.current?.focus();
  }, [reply]);

  async function submit() {
    const trimmed = body.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      await onSubmit(trimmed);
      setBody("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setErrorKey("comments.rateLimited");
      else if (err instanceof ApiError && err.code === "blocked") setErrorKey("comments.blocked");
      else setErrorKey("comments.error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="comment-composer"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <AutoTextarea
        ref={textareaRef}
        className="comment-input"
        value={body}
        maxLength={2000}
        rows={reply ? 2 : 3}
        placeholder={reply ? t("comments.replyPlaceholder") : t("comments.placeholder")}
        onChange={(event) => setBody(event.target.value)}
      />
      {errorKey && <p className="comments-status comments-status--error">{t(errorKey)}</p>}
      <div className="comment-composer-actions">
        {onCancel && (
          <button type="button" className="secondary" onClick={onCancel}>
            {t("comments.cancel")}
          </button>
        )}
        <button type="submit" disabled={busy || body.trim().length === 0}>
          {busy ? t("comments.posting") : reply ? t("comments.postReply") : t("comments.post")}
        </button>
      </div>
    </form>
  );
}

/**
 * Localized relative time in the reader's UI language, phrased as "3 дн. назад" /
 * "3 days ago" (the `short` style drops the bare "-3 дн." minus the narrow style
 * produced), with `numeric: "auto"` keeping "yesterday"/"вчера". Falls back to an
 * absolute date past ~30 days.
 */
function formatRelativeTime(iso: string, lang: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.round((Date.now() - then) / 1000);
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "short" });
  if (seconds < 60) return rtf.format(-seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return rtf.format(-minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hour");
  const days = Math.round(hours / 24);
  if (days < 30) return rtf.format(-days, "day");
  return new Date(iso).toLocaleDateString(lang);
}
